/**
 * Reads the widget's configuration off its own <script> tag.
 *
 *   <script src="https://feedback.example.com/widget.js"
 *           data-project="acme-staging"
 *           defer></script>
 *
 * The API base is derived from the script's own `src` by default, so a single
 * copy-pasted snippet works across every project with no second URL to keep in
 * sync. `data-api` overrides it for local development against a separate server.
 */

export interface BootConfig {
  projectId: string;
  apiBase: string;
  /** Override the server-provided launcher position. */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** When false, the launcher button is not rendered; open via the JS API. */
  showLauncher: boolean;
}

const POSITIONS = new Set(['bottom-right', 'bottom-left', 'top-right', 'top-left']);

function findOwnScript(): HTMLScriptElement | null {
  // `currentScript` is correct during synchronous execution and is what a
  // deferred classic script sees at run time.
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.project) {
    return current;
  }
  // Fallback for the async/injected case, where currentScript is null.
  return document.querySelector<HTMLScriptElement>('script[data-project][src*="widget"]');
}

export function readBootConfig(): BootConfig | null {
  const script = findOwnScript();
  if (!script) return null;

  const projectId = script.dataset.project?.trim();
  if (!projectId) return null;

  let apiBase = script.dataset.api?.trim();
  if (!apiBase) {
    try {
      // Strip the trailing "/widget.js" to get the service root.
      apiBase = new URL('.', script.src).href.replace(/\/$/, '');
    } catch {
      return null;
    }
  }
  apiBase = apiBase.replace(/\/$/, '');

  const rawPosition = script.dataset.position?.trim();
  const position = rawPosition && POSITIONS.has(rawPosition)
    ? (rawPosition as BootConfig['position'])
    : undefined;

  return {
    projectId,
    apiBase,
    ...(position ? { position } : {}),
    showLauncher: script.dataset.launcher !== 'false',
  };
}
